/**
 * Slug Normalization for Time-on-Task Tracking
 *
 * Replaces backend-generated ID segments (CUIDs, UUIDs) with placeholders to enable aggregation
 * of student tracking data across multiple attempts, while and preserving ids that will be shared across users like lesson IDs (e.g., 1-1-lesson-part-1).
 */

import { WRITE_YOUR_OWN_ACTIVITY_ID } from '@/lib/use-long-writes'

/**
 * Detects generated IDs based on format:
 * - Standard UUID: 8-4-4-4-12 hex format with hyphens (36 chars)
 * - MongoDB ObjectId: 24 consecutive hex characters
 * - CUID v2: 'c' followed by 24 lowercase alphanumeric (25 chars total)
 */
const GENERATED_ID_PATTERN =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{24}|c[a-z0-9]{24})$/i

/** Known readable IDs that match generated ID patterns but are shared activity identifiers */
const KNOWN_READABLE_IDS = new Set([WRITE_YOUR_OWN_ACTIVITY_ID])

/**
 * Checks if a segment looks like a generated ID (CUID, UUID, or ObjectId)
 */
export function isGeneratedSegment(segment: string): boolean {
  if (segment.length < 24) return false
  if (KNOWN_READABLE_IDS.has(segment)) return false
  return GENERATED_ID_PATTERN.test(segment)
}

/**
 * Normalizes a pathname by replacing generated ID segments with [id] placeholders.
 *
 * Preserves human-readable segments like curriculum IDs (1-1-lesson-part-1, map-1, etc).
 * Route groups are already invisible in the pathname.
 * Double slashes are collapsed intentionally.
 *
 * Examples:
 * - `/my-lessons/activity/1-1-lesson-part-1` → unchanged
 * - `/long-writes/550e8400.../6ba7b810.../snapshots/507f1f77...` → `/long-writes/[id]/[id]/snapshots/[id]`
 */
export function normalizePathForTracking(pathname: string): string {
  if (!pathname) return pathname

  const segments = pathname.split('/').filter((s) => s.length > 0)

  const normalizedSegments = segments.map((segment) => {
    return isGeneratedSegment(segment) ? '[id]' : segment
  })

  return '/' + normalizedSegments.join('/')
}
